import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Search, LayoutGrid, List as ListIcon, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import LeadCsvImport from "../components/LeadCsvImport";
import LeadDialog from "../components/crm/LeadDialog";
import LeadDetail from "../components/crm/LeadDetail";
import { daysSince } from "../components/crm/constants";

export default function CRM() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ref, setRef] = useState({ stages: [], sources: [], users: [] });
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    const params = { kind: "lead" };
    if (q) params.q = q;
    if (stageFilter !== "all") params.stage = stageFilter;
    if (ownerFilter !== "all") params.owner_id = ownerFilter;
    api.get("/crm/leads", { params }).then((r) => setLeads(r.data))
      .catch(() => setLeads([])).finally(() => setLoading(false));
  }, [q, stageFilter, ownerFilter]);

  useEffect(() => { api.get("/crm/reference").then((r) => setRef(r.data)); }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-open a lead when ?lead=<id> is present in the URL (e.g. from Notification bell)
  useEffect(() => {
    const lid = searchParams.get("lead");
    if (!lid || selected?.id === lid) return;
    api.get(`/crm/leads/${lid}`).then((r) => setSelected(r.data)).catch(() => {});
  }, [searchParams, selected?.id]);

  const stages = ref.stages || [];
  const usersById = useMemo(() => {
    const m = {}; (ref.users || []).forEach((u) => (m[u.id] = u)); return m;
  }, [ref]);

  const byStage = useMemo(() => {
    const m = {}; stages.forEach((s) => (m[s.key] = []));
    leads.forEach((l) => { if (m[l.stage]) m[l.stage].push(l); });
    return m;
  }, [leads, stages]);

  async function onCardDrop(leadId, newStage) {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage === newStage) return;
    try {
      await api.post(`/crm/leads/${leadId}/stage`, { stage: newStage });
      toast.success("Stage updated");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot update");
    }
  }

  async function exportCsv() {
    try {
      const params = { kind: "lead" };
      if (q) params.q = q;
      if (stageFilter !== "all") params.stage = stageFilter;
      if (ownerFilter !== "all") params.owner_id = ownerFilter;
      const r = await api.get("/crm/leads/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${leads.length} lead${leads.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Export failed");
    }
  }

  function auditBadgeClass(t) {
    if (t === "Certification") return "bg-[#0D1B2A] text-white";
    if (t === "Verification") return "bg-[#1A7F6E] text-white";
    return "bg-slate-200 text-slate-600";
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Sales pipeline</p>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">New enquiries · discovery calls · proposals. Convert to a client when they pay.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            <button type="button" data-testid="view-kanban" onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-[#0D1B2A] text-white" : "bg-white text-slate-600"}`}>
              <LayoutGrid className="w-4 h-4" />Kanban
            </button>
            <button type="button" data-testid="view-list" onClick={() => setView("list")}
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#0D1B2A] text-white" : "bg-white text-slate-600"}`}>
              <ListIcon className="w-4 h-4" />List
            </button>
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-csv-button">
            <Upload className="w-4 h-4 mr-2" />Import CSV
          </Button>
          <Button className="bg-[#1A7F6E] hover:bg-[#136053] text-white" onClick={() => setAddOpen(true)} data-testid="add-lead-button">
            <Plus className="w-4 h-4 mr-2" />Add lead
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organisation, contact, email…" className="pl-9" data-testid="crm-search" />
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-44" data-testid="filter-stage"><SelectValue placeholder="All stages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-44" data-testid="filter-owner"><SelectValue placeholder="All consultants" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All consultants</SelectItem>
              {(ref.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
      )}
      {!loading && view === "kanban" && (
        <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-x-auto pb-2">
          {stages.map((s) => (
            <div key={s.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onCardDrop(e.dataTransfer.getData("text/plain"), s.key)}
              className="bg-slate-100 rounded-lg p-3 min-h-[300px]" data-testid={`col-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{s.name}</span>
                <span className="text-xs font-semibold text-[#0D1B2A]">{byStage[s.key]?.length || 0}</span>
              </div>
              <div className="space-y-2">
                {(byStage[s.key] || []).map((l) => (
                  <button type="button" key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", l.id)}
                    onClick={() => setSelected(l)}
                    className="w-full text-left bg-white border border-slate-200 rounded-md p-3 shadow-sm cursor-pointer hover:border-[#1A7F6E]"
                    data-testid={`card-${l.id}`}>
                    <p className="text-sm font-semibold text-[#0D1B2A] truncate">{l.org_name}</p>
                    <p className="text-xs text-slate-500 truncate">{l.contact_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${auditBadgeClass(l.likely_audit_type)}`}>
                        {l.likely_audit_type || "Unknown"}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        {daysSince(l.stage_updated_at)}d · {usersById[l.owner_id]?.name?.split(" ")[0] || "—"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && view === "list" && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-3 px-2">Organisation</th>
                  <th className="text-left py-3 px-2">Contact</th>
                  <th className="text-left py-3 px-2">Stage</th>
                  <th className="text-left py-3 px-2 hidden md:table-cell">Audit</th>
                  <th className="text-left py-3 px-2 hidden lg:table-cell">Source</th>
                  <th className="text-left py-3 px-2 hidden md:table-cell">Owner</th>
                  <th className="text-left py-3 px-2 hidden sm:table-cell">Days in stage</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`row-${l.id}`}>
                    <td className="py-3 px-2 font-medium text-[#0D1B2A]">{l.org_name}</td>
                    <td className="py-3 px-2 text-slate-600">
                      {l.contact_name}<br />
                      <span className="text-xs text-slate-400">{l.contact_email}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-xs font-medium bg-[#E6F4F1] text-[#1A7F6E] px-2 py-0.5 rounded">
                        {stages.find((s) => s.key === l.stage)?.name || l.stage}
                      </span>
                    </td>
                    <td className="py-3 px-2 hidden md:table-cell text-slate-600">{l.likely_audit_type}</td>
                    <td className="py-3 px-2 hidden lg:table-cell text-slate-500">{l.lead_source || "—"}</td>
                    <td className="py-3 px-2 hidden md:table-cell text-slate-500">{usersById[l.owner_id]?.name || "—"}</td>
                    <td className="py-3 px-2 hidden sm:table-cell text-slate-500">{daysSince(l.stage_updated_at)}d</td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-slate-500 py-8">No leads match your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {addOpen && <LeadDialog ref_={ref} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); refresh(); }} />}
      <LeadCsvImport open={importOpen} onClose={() => setImportOpen(false)} onImported={refresh} />
      {selected && (
        <LeadDetail
          lead={selected}
          me={user}
          ref_={ref}
          onClose={() => { setSelected(null); if (searchParams.get("lead")) setSearchParams({}); }}
          onChanged={refresh}
          onConvert={(l) => nav(`/new?lead=${l.id}`)}
        />
      )}
    </div>
  );
}

